import { memo } from 'react'
import { X, SkipBack, SkipForward, Play, Pause, Loader2 } from 'lucide-react'
import type { EdgeDragMode } from './audioRemixTypes'
import { formatTime } from '../shared/AudioRemixShared'

interface MixerHeaderProps {
  modalTitle: string
  edgeDragMode: EdgeDragMode
  onEdgeDragModeChange: (mode: EdgeDragMode) => void
  playheadTime: number
  videoDuration: number
  isPlaying: boolean
  isSavingRemix: boolean
  hasChanges: boolean
  onPlay: () => void
  onPause: () => void
  onSeekToStart: () => void
  onSeekToEnd: () => void
  onReset: () => void
  onSave: () => void
  onClose: () => void
}

export const MixerHeader = memo(function MixerHeader({
  modalTitle,
  edgeDragMode,
  onEdgeDragModeChange,
  playheadTime,
  videoDuration,
  isPlaying,
  isSavingRemix,
  hasChanges,
  onPlay,
  onPause,
  onSeekToStart,
  onSeekToEnd,
  onReset,
  onSave,
  onClose,
}: MixerHeaderProps) {
  const MODES: Array<{ id: EdgeDragMode; label: string; activeClass: string }> = [
    { id: 'clip', label: 'Clip', activeClass: 'bg-white/10 text-white/85' },
    { id: 'fade', label: 'Fade', activeClass: 'bg-amber-500/[0.18] text-amber-300' },
    { id: 'speed', label: 'Speed', activeClass: 'bg-blue-500/[0.18] text-blue-300' },
  ]

  return (
    <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-border bg-surface-1 gap-3 shrink-0">
      {/* Left: title + dirty state */}
      <div className="flex items-center gap-2.5 min-w-0">
        <span className="text-[13px] font-semibold text-foreground whitespace-nowrap">{modalTitle}</span>
        {hasChanges && (
          <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/[0.12] text-amber-400 border border-amber-500/30 whitespace-nowrap">
            ● unsaved
          </span>
        )}
      </div>

      {/* Center: transport */}
      <div className="flex items-center gap-1.5 px-2 py-1 bg-surface-2 border border-border rounded-lg">
        <button onClick={onSeekToStart} className="p-1 rounded hover:bg-white/[0.06] text-text-secondary transition-colors" title="To start">
          <SkipBack className="w-3 h-3" />
        </button>
        <button onClick={isPlaying ? onPause : onPlay} className="p-1 rounded hover:bg-white/[0.06] text-foreground transition-colors" title={isPlaying ? 'Pause' : 'Play'}>
          {isPlaying ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
        </button>
        <button onClick={onSeekToEnd} className="p-1 rounded hover:bg-white/[0.06] text-text-secondary transition-colors" title="To end">
          <SkipForward className="w-3 h-3" />
        </button>
        <span className="text-[11px] font-mono tabular-nums text-foreground ml-1.5 min-w-[78px]">
          {formatTime(playheadTime)} / {formatTime(videoDuration)}
        </span>
      </div>

      {/* Right: mode toggle, save, close */}
      <div className="flex items-center gap-1.5">
        {/* Edge-drag mode */}
        <div className="flex p-0.5 rounded-md bg-surface-2 border border-border">
          {MODES.map((m) => (
            <button
              key={m.id}
              onClick={() => onEdgeDragModeChange(m.id)}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                edgeDragMode === m.id ? m.activeClass : 'text-white/30 hover:text-white/50'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div className="w-px h-4 bg-border mx-1" />

        <button
          onClick={onReset}
          className="px-2 py-1.5 rounded text-[11px] font-medium text-text-secondary hover:text-foreground hover:bg-surface-2 transition-all"
        >
          Reset
        </button>

        <button
          onClick={onSave}
          disabled={isSavingRemix}
          className={`px-3 py-1.5 rounded text-[11px] font-semibold transition-all ${
            isSavingRemix
              ? 'bg-surface-2 text-text-muted cursor-not-allowed'
              : 'bg-primary/80 hover:bg-primary text-foreground'
          }`}
        >
          {isSavingRemix ? (
            <span className="flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Remixing…</span>
          ) : 'Save & Remix'}
        </button>

        <button onClick={onClose} className="p-1.5 rounded hover:bg-white/[0.06] text-text-secondary transition-colors" title="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  )
})
